import { api, thunks } from "@app/api";
import { schema } from "@app/schema";
import type {
  DeployMaintenanceWindow,
  DeployMaintenanceWindowResponse,
} from "@app/types";

export const defaultMaintenanceWindowResponse = (
  m: Partial<DeployMaintenanceWindowResponse> = {},
): DeployMaintenanceWindowResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    day_of_week: "0",
    start_time_utc: "22:00",
    duration_hours: "5",
    created_at: now,
    updated_at: now,
    ...m,
  };
};

export const deserializeDeployMaintenanceWindow = (
  payload: DeployMaintenanceWindowResponse,
): DeployMaintenanceWindow => ({
  id: `${payload.id}`,
  dayOfWeek: Number(payload.day_of_week),
  startHour: Number(payload.start_time_utc.split(":")[0]),
  durationHours: Number(payload.duration_hours),
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

export const selectMaintenanceWindowById = schema.maintenanceWindows.selectById;

export const fetchMaintenanceWindowById = api.get<
  { id: string },
  DeployMaintenanceWindowResponse
>("/maintenance_windows/:id", function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  const mw = deserializeDeployMaintenanceWindow(ctx.json.value);
  yield* schema.update(schema.maintenanceWindows.add({ [mw.id]: mw }));
});

export interface MaintenanceWindowEditProps {
  dayOfWeek: number;
  startHour: number;
  durationHours: number;
}

const serializeMaintenanceWindowEditProps = (
  payload: MaintenanceWindowEditProps,
) => ({
  day_of_week: `${payload.dayOfWeek}`,
  start_time_utc: `${String(payload.startHour).padStart(2, "0")}:00`,
  duration_hours: `${payload.durationHours}`,
});

export interface ModifyMaintenanceWindowProps
  extends MaintenanceWindowEditProps {
  id: string;
  appId: string;
}

export const createMaintenanceWindowByAppId = api.post<
  ModifyMaintenanceWindowProps,
  DeployMaintenanceWindowResponse
>(["/apps/:appId/maintenance_window"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeMaintenanceWindowEditProps(ctx.payload)),
  });
  yield* next();
});

export const updateMaintenanceWindowByAppId = api.put<
  ModifyMaintenanceWindowProps,
  DeployMaintenanceWindowResponse
>(["/apps/:appId/maintenance_window"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeMaintenanceWindowEditProps(ctx.payload)),
  });
  yield* next();
});

export const saveMaintenanceWindow =
  thunks.create<ModifyMaintenanceWindowProps>(
    "save-maintenance-window",
    function* (ctx, next) {
      yield* schema.update(schema.loaders.start({ id: ctx.name }));
      const nextWindow = ctx.payload;
      const saveCtx = nextWindow.id
        ? yield* updateMaintenanceWindowByAppId.run(nextWindow)
        : yield* createMaintenanceWindowByAppId.run(nextWindow);

      yield* next();

      if (saveCtx.json.ok) {
        const mw = deserializeDeployMaintenanceWindow(saveCtx.json.value);
        yield* schema.update([
          schema.maintenanceWindows.add({ [mw.id]: mw }),
          schema.apps.patch({
            [nextWindow.appId]: { maintenanceWindowId: mw.id },
          }),
          schema.loaders.success({
            id: ctx.name,
            message: "Maintenance window saved",
          }),
        ]);
      } else {
        const data = saveCtx.json.error as Error;
        yield* schema.update(
          schema.loaders.error({ id: ctx.name, message: data.message }),
        );
      }
    },
  );

export interface ModifyDatabaseMaintenanceWindowProps
  extends MaintenanceWindowEditProps {
  id: string;
  databaseId: string;
}

export const createMaintenanceWindowByDatabaseId = api.post<
  ModifyDatabaseMaintenanceWindowProps,
  DeployMaintenanceWindowResponse
>(["/databases/:databaseId/maintenance_window"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeMaintenanceWindowEditProps(ctx.payload)),
  });
  yield* next();
});

export const updateMaintenanceWindowByDatabaseId = api.put<
  ModifyDatabaseMaintenanceWindowProps,
  DeployMaintenanceWindowResponse
>(["/databases/:databaseId/maintenance_window"], function* (ctx, next) {
  ctx.request = ctx.req({
    body: JSON.stringify(serializeMaintenanceWindowEditProps(ctx.payload)),
  });
  yield* next();
});

export const saveDatabaseMaintenanceWindow =
  thunks.create<ModifyDatabaseMaintenanceWindowProps>(
    "save-database-maintenance-window",
    function* (ctx, next) {
      yield* schema.update(schema.loaders.start({ id: ctx.name }));
      const nextWindow = ctx.payload;
      const saveCtx = nextWindow.id
        ? yield* updateMaintenanceWindowByDatabaseId.run(nextWindow)
        : yield* createMaintenanceWindowByDatabaseId.run(nextWindow);

      yield* next();

      if (saveCtx.json.ok) {
        const mw = deserializeDeployMaintenanceWindow(saveCtx.json.value);
        yield* schema.update([
          schema.maintenanceWindows.add({ [mw.id]: mw }),
          schema.databases.patch({
            [nextWindow.databaseId]: { maintenanceWindowId: mw.id },
          }),
          schema.loaders.success({
            id: ctx.name,
            message: "Maintenance window saved",
          }),
        ]);
      } else {
        const data = saveCtx.json.error as Error;
        yield* schema.update(
          schema.loaders.error({ id: ctx.name, message: data.message }),
        );
      }
    },
  );
